/**
 * ポリゴン基本演算 + 正多角形生成 + 変形（happa v1.1）。
 *
 * v1.1 の形状モデル（spec §3.2 / §3.6 / §5.3）を pure function で実装する。
 * 主な役割:
 *
 * - `generateInitialPolygon`: seed + jitter から正多角形を生成（spec §3.2）
 * - `subdividePolygon`: 辺を等分割してテキスト追従用の細分ポリゴンを得る
 * - `warpPolygonByCells`: 各頂点を最近傍セルの変位に追従させる（spec §3.6 / D-16）
 * - `offsetPolygonRadial`: 重心基準の法線方向へ offset（phantom ring / text margin）
 * - `pointInPolygon` / `polygonCentroid` / `polygonAABB` / `polygonArea`:
 *   基本演算ユーティリティ（spec §A.1 / wireframe 準拠）
 *
 * 全関数 pure。DOM / Canvas / 状態を参照しない。同 seed + 同 config →
 * 同結果の決定論性を保証する（NFR-06）。
 *
 * 設計参照:
 * - `docs/happa-designtool-v1.1-spec.md` §3.2（正多角形 + shape jitter）
 * - `docs/happa-designtool-v1.1-spec.md` §3.6（Warped Silhouette）
 * - `docs/happa-designtool-v1.1-spec.md` §5.3（Scene build）
 * - wireframe `wireframes/v0.10/wireframe.html` L367–525
 */

import { easeOutExpo } from './easing';
import type { Cell, Vec2 } from './types';

/**
 * polygon 用 RNG の seed オフセット（spec §3.2 / Q9 確定）。
 *
 * `AppState.seed`（0..1）を `Math.floor(seed × 1e9)` した整数値へ固定値
 * `54321` を加算して mulberry32 の初期状態とする。voronoi 用 RNG
 * （オフセットなし）と衝突しないよう分離する暗黙ルール。Phase C/D で
 * 独立 RNG インスタンスを渡す設計へ昇格検討（OQ-20）。
 */
const POLYGON_SEED_OFFSET = 54321;

/**
 * wireframe v0.10 L355–364 と同じ mulberry32 を整数 seed で駆動する。
 *
 * `rng.ts#createRng` は float seed（0..1）を受ける API のため、整数 seed
 * を直接渡す経路として本関数をローカルに持つ。挙動は `createRng` と
 * **完全に同一**で、初期状態の投入経路のみが異なる。
 *
 * @param state 32bit 整数の初期状態（`Math.floor(seed × 1e9) + offset`）。
 */
function mulberry32FromInt(state: number): () => number {
  let a = state | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** `generateInitialPolygon` のパラメータ。 */
export interface GenerateInitialPolygonParams {
  /** 頂点数（整数、3..24 の範囲を想定。spec §3.2 では離散 10 段階だが core は任意整数を受ける）。 */
  vertexCount: number;
  /** Shape Jitter 強度（0..100%）。0 = 正多角形、100 = 角度・半径最大ランダム。 */
  shapeRandomness: number;
  /** `AppState.seed`（0..1 の float）。内部で `Math.floor(seed × 1e9) + 54321` に変換。 */
  seed: number;
  /** 内接楕円の中心 X（outer 座標系、px）。 */
  centerX: number;
  /** 内接楕円の中心 Y（outer 座標系、px）。 */
  centerY: number;
  /**
   * innerCanvas の幅（px）。wireframe L415 `rx = (innerX1 - innerX0) / 2` に相当。
   * `rx = innerW / 2 × (shapeScale / 100)` として内接楕円の X 半径に変換される。
   */
  innerW: number;
  /**
   * innerCanvas の高さ（px）。wireframe L416 `ry = (innerY1 - innerY0) / 2` に相当。
   * `ry = innerH / 2 × (shapeScale / 100)` として内接楕円の Y 半径に変換される。
   */
  innerH: number;
  /**
   * innerPoly のサイズ倍率（%、30..100）。`AppState.shapeScale` を素通しで渡す。
   *
   * - 100: 内接楕円に最大内接（rx = innerW/2、ry = innerH/2）。
   * - 70 （default）: 内接楕円の 70%。爆破時に破片が innerPoly 周囲へ飛び出す
   *   余白を確保するため、innerCanvas 全体を埋めずに中央に寄せるための縮小。
   * - 30 （下限）: 極端に小さい innerPoly（デザイン検証用）。
   *
   * 値は UI slider が整数で書き込む前提だが、core は任意 float を受ける
   * （clamp は呼び出し側の責務）。0 以下を渡すと退化した「点」になるため、
   * 呼び出し側で 30..100 に clamp することを推奨。
   */
  shapeScale: number;
}

/**
 * 正多角形 + shape jitter を生成する（spec §3.2、wireframe L412–434 と同等）。
 *
 * 偶数面は頂点を半ステップ斜めに回転（四角形が軸平行になるのを避ける）、
 * 奇数面は上向きから開始する。各頂点は `(rng - 0.5) × (2π/N) × 0.4 × rAmp`
 * の角度ジッタと `1 - rng × rAmp × 0.45` の半径倍率で揺らす。
 * `rAmp = shapeRandomness / 100`。
 *
 * RNG は `mulberry32(floor(seed × 1e9) + 54321)` を内部生成（Q9 確定）。
 * 呼び出し側の RNG と衝突せず、同 `seed` + 同パラメータで同結果を返す。
 *
 * **楕円半径 + shapeScale**（Phase C さらなる修正ラウンド Step 2）:
 * wireframe L415–416 と同じく `rx = innerW / 2`、`ry = innerH / 2` を楕円半径
 * として用い、`scale = shapeScale / 100` を乗じる。頂点座標は
 * `x = centerX + cos(angle) × rx × scale × radiusMult`、
 * `y = centerY + sin(angle) × ry × scale × radiusMult` で生成される。
 * aspect 比が 1:1 以外のキャンバスでも innerPoly が楕円形状になる
 * （従来の `radius` 単一版は等方円で、横長キャンバスで縦方向に余白が
 * 過剰に残る問題があった）。
 *
 * @param params 生成パラメータ。
 * @returns `vertexCount` 個の頂点列。CCW/CW は `startAngle` の回り方次第
 *          （本実装は時計回り: i 増加で angle 増加）。
 */
export function generateInitialPolygon(params: GenerateInitialPolygonParams): Vec2[] {
  const { vertexCount, shapeRandomness, seed, centerX, centerY, innerW, innerH, shapeScale } =
    params;
  // wireframe L417 `mulberry32(Math.floor(seed * 1e9) + 54321)` と bit 単位で同値。
  const rng = mulberry32FromInt(Math.floor(seed * 1e9) + POLYGON_SEED_OFFSET);
  const rAmp = shapeRandomness / 100;
  // scale は 0.30..1.00 を想定（UI clamp 済み前提）。ゼロ除算や負値への
  // 防御は呼び出し側の責務。
  const scale = shapeScale / 100;
  const rx = (innerW / 2) * scale;
  const ry = (innerH / 2) * scale;
  const startAngle = vertexCount % 2 === 0 ? -Math.PI / 2 - Math.PI / vertexCount : -Math.PI / 2;

  const poly: Vec2[] = [];
  for (let i = 0; i < vertexCount; i++) {
    const baseAngle = startAngle + (i / vertexCount) * Math.PI * 2;
    const angleJit = (rng() - 0.5) * ((Math.PI * 2) / vertexCount) * 0.4 * rAmp;
    const angle = baseAngle + angleJit;
    const radiusMult = 1 - rng() * rAmp * 0.45;
    poly.push([
      centerX + Math.cos(angle) * rx * radiusMult,
      centerY + Math.sin(angle) * ry * radiusMult,
    ]);
  }
  return poly;
}

/**
 * 各辺を `perEdge` 等分に細分化する（wireframe L436–448 と同等）。
 *
 * spec §3.6: テキスト追従のため innerPoly を subdivide してから warp する。
 * `perEdge = max(6, floor(72 / N))` を推奨（spec §3.6 表）。本関数は
 * `perEdge` 個の補間点を各辺に配置し、総頂点数 `N × perEdge` の閉じた
 * 多角形を返す。始点は含むが終点は含まない（次辺の始点と重複するため）。
 *
 * @param polygon 元の多角形（3 頂点以上）。
 * @param perEdge 1 辺あたりの分割数（デフォルト 12 = N=6 相当）。1 未満は未定義。
 * @returns 細分化後の頂点列（長さ `polygon.length × perEdge`）。
 */
export function subdividePolygon(polygon: Vec2[], perEdge = 12): Vec2[] {
  const out: Vec2[] = [];
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % n];
    if (!a || !b) continue;
    for (let s = 0; s < perEdge; s++) {
      const t = s / perEdge;
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  }
  return out;
}

/**
 * `warpPolygonByCells` 影響半径係数 k（Phase C 修正ラウンド Step 2 / Bug 1）。
 *
 * 影響半径 `R = avgCellRadius × k` を決める倍率。k=2.0 は「seed 1 個分の円
 * （avgCellRadius）」を直径で覆える大きさの影響圏を意味する。小さすぎると
 * vertex が直下の cell しか追わず silhouette が破片群の最外輪郭から取り残され、
 * 大きすぎると遠方破片の変位まで拾って silhouette が過剰に膨らむ。k=2.0 は
 * その中間で、破片数 20〜240 の範囲で silhouette が破片群をほどよく包む値。
 *
 * 調整余地: 将来 `state.warpInfluenceK` 等でパラメータ化する可能性あり
 * （現段階では定数化し、ユーザ目視確認の結果次第で昇格判断）。
 */
const WARP_INFLUENCE_K = 2.0;

/**
 * 各頂点を影響圏内 cell の**最大変位**に追従させる（spec §3.6 / D-16、
 * Phase C 修正ラウンド Step 2 で最近傍方式から強化）。
 *
 * アルゴリズム（影響圏内最大変位方式）:
 * 1. `p = easeOutExpo(progress)`
 * 2. `avgCellRadius = sqrt(polygonArea(polygon) / cells.length / π)`
 * 3. `R = avgCellRadius × WARP_INFLUENCE_K`
 * 4. 各 vertex v について、centroid までの距離が `R` 以下の cell すべてを対象に
 *    `maxVx`, `maxVy` を「符号付きで絶対値が最大の変位」として集める
 *    （`|c.vx × p| > |maxVx|` なら `maxVx = c.vx × p`、Y 成分も同様）
 * 5. `v_new = v + (maxVx, maxVy)`
 *
 * **最近傍方式との違い** (旧実装):
 * 旧は `vertex` の最近傍 cell 1 個の変位のみを反映していたため、複数 cell が
 * 同方向へ飛ぶ場合でも 1 個分の変位しか取らず、silhouette が破片群の最外輪郭
 * から取り残されていた（Bug 1 の根因の一つ）。最大変位方式は影響圏内全 cell
 * の中で「最も遠くへ飛ぶ変位」を採用するため、silhouette が破片群全体を
 * 外包しやすくなる。
 *
 * **phantom cell の扱い**:
 * `cells` 内に `cell.isPhantom === true` が混じっていた場合、本関数は内部で
 * **保険として filter する**。呼び出し側で filter 済みの配列を渡すのが
 * 推奨経路（renderer.ts がそうしている）だが、呼び出し漏れで silhouette が
 * phantom の仮想変位に引っ張られる事故を避けるため二重防御する。
 *
 * **早期 return**:
 * - `p === 0` → 入力 polygon のコピーを返す（progress=0 ではあらゆる cell 変位が 0）
 * - filter 後の cells が空 → 入力 polygon のコピーを返す
 * - `avgCellRadius <= 0`（polygon area が 0 など退化入力）→ 入力のコピーを返す
 *
 * 回転（`cell.rot`）はテキスト経路には適用しない（spec §3.6 の式通り）。
 * 決定論性: 同じ入力で同じ出力（副作用なし、RNG 不使用）。
 *
 * @param polygon subdivided 済みの innerPoly。
 * @param cells `Scene.cells`（real + phantom 混在可、内部で phantom は除外）。
 * @param progress アニメーション進行度（0..1）。
 * @returns warp 後の頂点列（長さは入力と同じ）。
 */
export function warpPolygonByCells(polygon: Vec2[], cells: Cell[], progress: number): Vec2[] {
  const p = easeOutExpo(progress);
  if (p === 0) return polygon.slice();
  // 二重防御の filter（呼び出し側で済んでいても再度除去、破壊的ではないので
  // 僅かなコスト）。Bug 1 修正の一環で phantom の変位を silhouette に反映させない。
  const realCells = cells.filter((c) => !c.isPhantom);
  if (realCells.length === 0) return polygon.slice();

  // 影響半径 R を polygon 面積とセル数から算出。avgCellRadius が 0 以下になる
  // 退化入力（area=0 の polygon 等）では変位を掛けられないので早期 return。
  const area = polygonArea(polygon);
  const avgCellRadius = Math.sqrt(area / realCells.length / Math.PI);
  if (!(avgCellRadius > 0)) return polygon.slice();
  const R = avgCellRadius * WARP_INFLUENCE_K;
  const R2 = R * R;

  return polygon.map((vertex): Vec2 => {
    let maxVx = 0;
    let maxVy = 0;
    for (const cell of realCells) {
      const dx = vertex[0] - cell.centroid[0];
      const dy = vertex[1] - cell.centroid[1];
      const d2 = dx * dx + dy * dy;
      if (d2 > R2) continue;
      const cvx = cell.vx * p;
      const cvy = cell.vy * p;
      // 符号付きで絶対値最大の変位を採用（「破片群の最外輪郭」を追うため、
      // 方向は保ちつつ最大移動量を取る）。
      if (Math.abs(cvx) > Math.abs(maxVx)) maxVx = cvx;
      if (Math.abs(cvy) > Math.abs(maxVy)) maxVy = cvy;
    }
    return [vertex[0] + maxVx, vertex[1] + maxVy];
  });
}

/**
 * 重心基準の法線方向に `distance` px オフセットする（wireframe L382–390）。
 *
 * 各頂点と重心（幾何平均）を結ぶ単位ベクトル方向に `distance` を加える。
 * `distance > 0` で外側に膨らみ、`distance < 0` で内側に縮む。
 * phantom ring 構築や text margin 膨張に使う（spec §5.3 の `shrunkPoly` /
 * `phantomOffset`、spec §5.4.1 の `rawTextPoly`）。
 *
 * 幾何学的重心（面積重み）ではなく**単純平均**を採用するのは wireframe と
 * の挙動一致を優先するため。凸多角形 + 軽微な jitter 前提では差異は小さい。
 *
 * 頂点数が 3 未満の場合は入力のコピーを返す（退化入力の安全側）。
 *
 * @param polygon 元の多角形。
 * @param distance オフセット距離（px、符号付き）。
 * @returns オフセット後の頂点列（長さは入力と同じ）。
 */
export function offsetPolygonRadial(polygon: Vec2[], distance: number): Vec2[] {
  if (polygon.length < 3) return polygon.slice();
  const [cx, cy] = polygonCentroid(polygon);
  return polygon.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return [x + (dx / len) * distance, y + (dy / len) * distance];
  });
}

/**
 * 点が polygon 内部か判定する（ray casting、wireframe L367–376）。
 *
 * 水平半直線との交差回数の偶奇で判定。境界上の挙動は未定義（実装上は
 * 「含まれる/含まれない」のどちらかに偏る）。凸・凹どちらにも動作する。
 *
 * @param point 判定対象の点。
 * @param polygon 閉じた多角形（自動で閉じる）。
 * @returns 内部なら `true`、外部 or 境界上なら `false`（偶奇に依存）。
 */
export function pointInPolygon(point: Vec2, polygon: Vec2[]): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    if (!pi || !pj) continue;
    const [xi, yi] = pi;
    const [xj, yj] = pj;
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * polygon の**単純平均**による重心（wireframe L377–381）。
 *
 * 面積重み付き centroid（shoelace）ではなく、頂点座標の算術平均を返す。
 * `offsetPolygonRadial` と `generatePhantomRing` が参照する重心と一致
 * させるため本実装を採用（wireframe と同挙動）。厳密な面積重心が必要な
 * 場合は呼び出し側で `polygonArea` と shoelace を組み合わせること。
 *
 * 頂点数 0 の場合は `[NaN, NaN]` を返す（入力側で弾くべき退化ケース）。
 *
 * @param polygon 多角形（頂点 1 個以上）。
 * @returns 重心座標 `[x, y]`。
 */
export function polygonCentroid(polygon: Vec2[]): Vec2 {
  let cx = 0;
  let cy = 0;
  for (const [x, y] of polygon) {
    cx += x;
    cy += y;
  }
  const n = polygon.length;
  return [cx / n, cy / n];
}

/**
 * 指定の基準点から polygon 頂点までの**最大距離**を返す（Phase K1）。
 *
 * 用途: 破片配置の radial 正規化（`t = r / rMax` で中心からの正規化距離を
 * 計算、`0..1` クランプ）。`scene.ts#buildOneCell` が cell の radial
 * 位置を求めるのに使う。Breath Motion 側（`breath.ts`）でも将来的に
 * 同じ基準値が必要になれば再利用できるよう public 化している。
 *
 * `reference` 省略時は `polygonCentroid(polygon)`（算術平均）を基準とする。
 * 呼び出し側で面積重心や任意の点（innerCX/innerCY 等）を渡すのも可。
 *
 * 頂点数 0 の場合は 0 を返す（退化入力の安全側）。
 *
 * @param polygon 多角形（頂点 1 個以上）。
 * @param reference 基準点。省略時は `polygonCentroid(polygon)`。
 * @returns `max_i |polygon[i] - reference|`（px、非負）。
 */
export function polygonMaxRadius(polygon: Vec2[], reference?: Vec2): number {
  const n = polygon.length;
  if (n === 0) return 0;
  const [cx, cy] = reference ?? polygonCentroid(polygon);
  let maxR2 = 0;
  for (const [x, y] of polygon) {
    const dx = x - cx;
    const dy = y - cy;
    const r2 = dx * dx + dy * dy;
    if (r2 > maxR2) maxR2 = r2;
  }
  return Math.sqrt(maxR2);
}

/**
 * Axis-aligned bounding box を返す（wireframe L391–400）。
 *
 * @param polygon 多角形（頂点 1 個以上）。
 * @returns `[minX, minY, maxX, maxY]`。空入力では `[+∞, +∞, -∞, -∞]`。
 */
export function polygonAABB(polygon: Vec2[]): [number, number, number, number] {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const [x, y] of polygon) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return [minX, minY, maxX, maxY];
}

/**
 * 点を AABB でクランプした新しい `Vec2` を返す（Phase C Bug-2 対策）。
 *
 * Voronoi `cellPolygon(i)` が返すセル頂点は、phantom リングの広がりに応じて
 * innerPoly の AABB を大きくはみ出した座標を含むことがある。Path2D のクリップ
 * 描画で最終的に落ちるとはいえ、レンダ段で扱う座標値を有界化しておくと
 * 数値的な破綻（下流の計算で `Infinity` 混入、shoelace の桁落ち等）を回避
 * できる。`clampVec2` はそのための最小ユーティリティ。
 *
 * `aabb` が `[minX, minY, maxX, maxY]` の形で渡されることを期待する
 * （`polygonAABB` の戻り値と完全互換）。`minX > maxX` のような退化 AABB では
 * クランプ結果が定義されないため、呼び出し側で弾くこと。
 *
 * 副作用なし：入力の `Vec2` は変更しない。新しいタプルを返す。
 *
 * @param point クランプしたい点。
 * @param aabb `[minX, minY, maxX, maxY]`。
 * @returns 各成分を `[minX, maxX]` / `[minY, maxY]` にクランプした新しい `Vec2`。
 */
export function clampVec2(point: Vec2, aabb: [number, number, number, number]): Vec2 {
  const [minX, minY, maxX, maxY] = aabb;
  const [x, y] = point;
  return [Math.max(minX, Math.min(maxX, x)), Math.max(minY, Math.min(maxY, y))];
}

/**
 * 符号なし面積（shoelace、wireframe L401–410）。
 *
 * Spec §3.4 の `avgCellRad = sqrt(polygonArea / fragmentCount / π)` に用いる。
 * 頂点数 3 未満では 0 を返す（退化入力）。
 *
 * @param polygon 多角形（頂点 3 個以上）。
 * @returns 符号なし面積（px²）。
 */
export function polygonArea(polygon: Vec2[]): number {
  const n = polygon.length;
  if (n < 3) return 0;
  let a = 0;
  for (let i = 0; i < n; i++) {
    const pi = polygon[i];
    const pj = polygon[(i + 1) % n];
    if (!pi || !pj) continue;
    a += pi[0] * pj[1] - pj[0] * pi[1];
  }
  return Math.abs(a) / 2;
}
